import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, assetUrl, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SiteFooter } from "@/components/Chrome";
import { Disclosure } from "@/components/Provenance";

const Decide = ({ x, onDone }) => {
  const [busy, setBusy] = useState(false);
  const decided = /^(Approved|Declined)/.test(x.ownerStatus);
  const go = async (decision) => {
    setBusy(true);
    try { await api.post(`/proposals/${x.id}/decision`, { decision, note: "" }); toast(decision === "approve" ? "Approved (self-attested)" : "Declined — pledges released"); onDone(); } catch (e) { toast(errMsg(e, "Something went wrong.")); }
    setBusy(false);
  };
  if (decided) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <button className="btn-primary" disabled={busy} onClick={() => go("approve")} data-testid={`owner-view-approve-${x.id}`}>Approve</button>
      <button className="btn-ghost" disabled={busy} onClick={() => go("decline")} data-testid={`owner-view-decline-${x.id}`}>Decline</button>
    </div>
  );
};

const Card = ({ x, onDone }) => (
  <article className="grid gap-6 border-t border-[var(--line)] py-10 md:grid-cols-[2fr_3fr]" data-testid={`owner-proposal-${x.id}`}>
    <Link to={`/m/${x.id}`} className="relative block aspect-[4/3] overflow-hidden rounded-xl bg-[var(--bone-2)]">
      {x.visionUrl && <img src={assetUrl(x.visionUrl)} alt="Vision" className="h-full w-full object-cover" />}
      <Disclosure small />
    </Link>
    <div className="grid content-start gap-5">
      <div>
        <div className="eyebrow">{x.propertyTitle}</div>
        <Link to={`/m/${x.id}`} className="mt-1 block font-display text-3xl tracking-tight hover:underline" data-testid={`owner-proposal-link-${x.id}`}>{x.proposer.label}</Link>
        <p className="mt-1 text-sm text-[var(--stone-dark)]" data-testid={`owner-proposal-status-${x.id}`}>{x.ownerStatus} · {x.claimLabel}{x.multipleClaims ? " · Multiple owner claims" : ""}</p>
      </div>
      <div>
        <p className="font-display text-2xl">${x.support.total.toLocaleString()}</p>
        <p className="text-xs text-[var(--stone-dark)]" data-testid={`owner-proposal-support-${x.id}`}>{x.support.label} · Funds collected: $0</p>
        {x.testFunds && <p className="text-xs text-[var(--stone-dark)]" data-testid={`owner-proposal-test-funds-${x.id}`}>{x.testFunds.label}</p>}
      </div>
      {x.priorities.length > 0 && (
        <ol className="grid gap-1 text-sm" data-testid={`owner-proposal-priorities-${x.id}`}>
          {x.priorities.map((pr, i) => <li key={pr.id} className="flex justify-between gap-3"><span>{i + 1}. {pr.text}</span><span className="text-[var(--stone-dark)]">{pr.votes} ▲</span></li>)}
        </ol>
      )}
      {x.budget ? (
        <div className="grid grid-cols-3 gap-3 text-sm" data-testid={`owner-proposal-budget-${x.id}`}>
          <div><div className="eyebrow">Verified product prices</div><div>${x.budget.verifiedTotal.toLocaleString()}</div></div>
          <div><div className="eyebrow">Estimates</div><div>{x.budget.estimates.length} items</div></div>
          <div><div className="eyebrow">Professional quotes</div><div>{x.budget.quotes.length} · Quote required</div></div>
        </div>
      ) : <p className="text-sm text-[var(--stone-dark)]">Package didn&apos;t finish.</p>}
      <Decide x={x} onDone={onDone} />
    </div>
  </article>
);

export default function OwnerView() {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/me/owner").then((r) => setData(r.data)).catch(() => setData({ claims: 0, proposals: [] })), []);
  useEffect(() => { if (auth) load(); }, [auth, load]);
  return (
    <main data-testid="owner-view-page">
      <div className="mx-auto max-w-6xl px-5 pt-10 sm:px-8">
        <div className="eyebrow mb-3">Owner view</div>
        <h1 className="font-display text-4xl leading-[1.05] tracking-tight sm:text-5xl">My places as owner</h1>
        <p className="mt-3 max-w-2xl text-sm text-[var(--stone-dark)]">Proposals for properties you&apos;ve claimed. Claims are self-attested — not proof of ownership. No money is collected here.</p>
        {!auth && <p className="mt-10 text-sm" data-testid="owner-view-signed-out">Sign in to see the places you&apos;ve claimed.</p>}
        {auth && !data && <div className="mt-10 h-48 rounded bg-[var(--bone-2)] shimmer" />}
        {data && !data.proposals.length && <p className="mt-10 text-sm" data-testid="owner-view-empty">No proposals yet for places you&apos;ve claimed.</p>}
        <div className="mt-10">{data?.proposals.map((x) => <Card key={x.id} x={x} onDone={load} />)}</div>
      </div>
      <SiteFooter />
    </main>
  );
}
